import { FACE_CONFIG, FACE_ORDER } from './cube-config.js';
import { STICKERS_PER_FACE_4X4, gridToIndex4x4, indexToGrid4x4 } from './cube-config-4x4.js';

export const MOVE_FACES_4X4 = Object.freeze(['U', 'R', 'F', 'D', 'L', 'B']);
export const MOVE_KINDS_4X4 = Object.freeze(['outer', 'inner', 'wide']);

const FACE_MOVE_NAMES = Object.freeze({ U: '顶', R: '右', F: '前', D: '底', L: '左', B: '后' });

function dot(a, b) {
  return a.reduce((sum, value, index) => sum + value * b[index], 0);
}

function cross(a, b) {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function add(...vectors) {
  return vectors[0].map((_, index) => vectors.reduce((sum, vector) => sum + vector[index], 0));
}

function scale(vector, amount) {
  return vector.map((value) => value * amount);
}

function rotateVector(vector, axis, amount) {
  const angle = -amount * Math.PI / 2;
  const cosine = Math.round(Math.cos(angle));
  const sine = Math.round(Math.sin(angle));
  return add(
    scale(vector, cosine),
    scale(cross(axis, vector), sine),
    scale(axis, dot(axis, vector) * (1 - cosine)),
  ).map((value) => Math.round(value));
}

function symbolFor(face, kind, amount) {
  const prefix = kind === 'inner' ? '2' : '';
  const wide = kind === 'wide' ? 'w' : '';
  const suffix = amount === 2 ? '2' : amount === 3 ? "'" : '';
  return `${prefix}${face}${wide}${suffix}`;
}

export function parseMove4x4(move) {
  if (typeof move !== 'string') throw new TypeError('动作必须是字符串');
  const token = move.trim();
  const match = /^(?:2([URFDLB])|([URFDLB])w|([urfdlb])|([URFDLB]))(2|')?$/.exec(token);
  if (!match) throw new TypeError(`不支持的 4×4 动作：${move}`);
  const [, explicitInner, wideFace, lowerFace, outerFace, suffix = ''] = match;
  const face = explicitInner ?? wideFace ?? lowerFace?.toUpperCase() ?? outerFace;
  const kind = explicitInner || lowerFace ? 'inner' : wideFace ? 'wide' : 'outer';
  const amount = suffix === '2' ? 2 : suffix === "'" ? 3 : 1;
  return Object.freeze({ face, kind, amount, symbol: symbolFor(face, kind, amount) });
}

export function normalizeMove4x4(move) {
  const parsed = typeof move === 'string' ? parseMove4x4(move) : move;
  const amount = ((parsed.amount % 4) + 4) % 4;
  if (amount === 0) return null;
  if (!MOVE_FACES_4X4.includes(parsed.face) || !MOVE_KINDS_4X4.includes(parsed.kind)) {
    throw new TypeError('4×4 动作对象缺少合法的 face 或 kind');
  }
  return symbolFor(parsed.face, parsed.kind, amount);
}

export function invertMove4x4(move) {
  const parsed = parseMove4x4(move);
  return normalizeMove4x4({ ...parsed, amount: 4 - parsed.amount });
}

export function parseAlgorithm4x4(formula) {
  if (typeof formula !== 'string') throw new TypeError('公式必须是字符串');
  const trimmed = formula.trim();
  return trimmed ? trimmed.split(/\s+/).map((token) => parseMove4x4(token).symbol) : [];
}

export function moveDescription4x4(move) {
  const { face, kind, amount } = parseMove4x4(move);
  const layer = kind === 'wide' ? `${FACE_MOVE_NAMES[face]}侧两层` : kind === 'inner' ? `${FACE_MOVE_NAMES[face]}侧内层` : `${FACE_MOVE_NAMES[face]}外层`;
  if (amount === 2) return `${layer}旋转 180°`;
  return `${layer}${amount === 1 ? '顺时针' : '逆时针'}旋转 90°`;
}

export function stickerCubiePosition4x4(face, index) {
  const { row, col } = indexToGrid4x4(index);
  const { normal, right, down } = FACE_CONFIG[face];
  return normal.map((value, axis) => (
    value * 3 + right[axis] * (col * 2 - 3) + down[axis] * (row * 2 - 3)
  ));
}

function faceAndIndexFromVectors(normal, position) {
  const face = FACE_ORDER.find((candidate) => FACE_CONFIG[candidate].normal.every((value, axis) => value === normal[axis]));
  if (!face) throw new Error(`无法从法向量恢复 4×4 魔方面：${normal.join(',')}`);
  const { right, down } = FACE_CONFIG[face];
  const col = Math.round((dot(position, right) + 3) / 2);
  const row = Math.round((dot(position, down) + 3) / 2);
  return { face, index: gridToIndex4x4(row, col) };
}

export function coordinateBelongsToMove4x4(position, move) {
  const parsed = typeof move === 'string' ? parseMove4x4(move) : move;
  const distance = dot(position, FACE_CONFIG[parsed.face].normal);
  if (parsed.kind === 'outer') return distance === 3;
  if (parsed.kind === 'inner') return distance === 1;
  return distance >= 1;
}

export function stickerBelongsToMoveLayer4x4(face, index, move) {
  return coordinateBelongsToMove4x4(stickerCubiePosition4x4(face, index), move);
}

export function mapStickerThroughMove4x4(face, index, move) {
  const parsed = typeof move === 'string' ? parseMove4x4(move) : move;
  if (!stickerBelongsToMoveLayer4x4(face, index, parsed)) return { face, index };
  const axis = FACE_CONFIG[parsed.face].normal;
  return faceAndIndexFromVectors(
    rotateVector(FACE_CONFIG[face].normal, axis, parsed.amount),
    rotateVector(stickerCubiePosition4x4(face, index), axis, parsed.amount),
  );
}

function cloneStickers(stickers) {
  return Object.fromEntries(FACE_ORDER.map((face) => [face, [...stickers[face]]]));
}

export function applyMoveToState4x4(stickers, move) {
  const parsed = typeof move === 'string' ? parseMove4x4(move) : move;
  const next = cloneStickers(stickers);
  for (const face of FACE_ORDER) {
    for (let index = 0; index < STICKERS_PER_FACE_4X4; index += 1) {
      if (!stickerBelongsToMoveLayer4x4(face, index, parsed)) continue;
      const destination = mapStickerThroughMove4x4(face, index, parsed);
      next[destination.face][destination.index] = stickers[face][index];
    }
  }
  return next;
}

export function applyAlgorithmToState4x4(stickers, formulaOrMoves) {
  const moves = Array.isArray(formulaOrMoves) ? formulaOrMoves : parseAlgorithm4x4(formulaOrMoves);
  return moves.reduce((state, move) => applyMoveToState4x4(state, move), cloneStickers(stickers));
}

export function getMoveAnimationSpec4x4(move) {
  const parsed = typeof move === 'string' ? parseMove4x4(move) : move;
  const axis = FACE_CONFIG[parsed.face].normal;
  const axisIndex = axis.findIndex((value) => value !== 0);
  const sign = axis[axisIndex];
  const layers = parsed.kind === 'outer' ? [3] : parsed.kind === 'inner' ? [1] : [1, 3];
  return Object.freeze({
    ...parsed,
    axis: Object.freeze([...axis]),
    axisIndex,
    layerCoordinates: Object.freeze(layers.map((layer) => layer * sign)),
    angleRadians: parsed.amount === 3 ? Math.PI / 2 : -parsed.amount * Math.PI / 2,
  });
}

